/**
 * <sl-table> — logic layer for table-by studiolab
 * (blocks/sl-table.liquid).
 *
 * Responsibilities:
 * - Build the mobile selector UI (pills / carousel / dropdown) from the
 *   rendered child images' data-option-name attributes (child theme blocks
 *   are isolated — the parent can't read their settings).
 * - Preload images when the block nears the viewport (IntersectionObserver).
 * - Animate the image swap. Motion is self-contained: it uses GSAP when the
 *   host theme already loads it (feature-detected as window.gsap — NEVER
 *   imported); with no GSAP or under prefers-reduced-motion it swaps instantly.
 * - Optional scroll-entrance reveal (built-in IntersectionObserver; no external
 *   motion palette). The <sl-table> ships the reveal state class from Liquid;
 *   this adds .is-revealed when the block scrolls in.
 * - Theme Editor: selecting a child option block jumps to its image.
 *
 * Zero dependencies. Fully namespaced (.sl-table__*).
 */

/* CSS timing keywords (Theme Editor settings) → GSAP eases. */
const SL_TABLE_EASE_MAP = {
  ease: 'power1.inOut',
  'ease-in': 'power1.in',
  'ease-out': 'power1.out',
  'ease-in-out': 'power1.inOut',
  linear: 'none',
};

class SLTable extends HTMLElement {
  #images = [];
  #currentIndex = 0;
  #isAnimating = false;
  #preloaded = false;
  #io = null;
  #revealIo = null;
  #onBlockSelect = null;

  connectedCallback() {
    this.#images = Array.from(this.querySelectorAll('.sl-table__image'));
    if (!this.#images.length) return;

    const wrapper = this.querySelector('[data-sl-images]');
    if (wrapper) wrapper.classList.add('is-initialized');
    this.#images[0].classList.add('active');

    this.#buildSelector();
    this.#setupPreload();
    this.#setupReveal();

    this.#onBlockSelect = (event) => {
      const img = event.target && event.target.closest && event.target.closest('.sl-table__image');
      if (!img || !this.contains(img)) return;
      const index = this.#images.indexOf(img);
      if (index >= 0) this.#switchTo(index, null, true);
    };
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
  }

  disconnectedCallback() {
    if (this.#io) this.#io.disconnect();
    if (this.#revealIo) this.#revealIo.disconnect();
    if (this.#onBlockSelect) {
      document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    }
  }

  #optionNames() {
    return this.#images.map((img) => img.dataset.optionName || '');
  }

  #reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ---------------- selector construction ---------------- */

  #buildSelector() {
    const style = this.dataset.selectorStyle;
    if (style === 'pills') this.#buildPills();
    if (style === 'carousel') this.#buildCarousel();
    if (style === 'dropdown') this.#buildDropdown();
  }

  #buildPills() {
    const container = this.querySelector('[data-sl-pills]');
    if (!container) return;
    container.replaceChildren();
    this.#optionNames().forEach((name, index) => {
      if (!name) return;
      const pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'sl-table__pill' + (index === 0 ? ' active' : '');
      pill.textContent = name;
      pill.setAttribute('aria-pressed', index === 0 ? 'true' : 'false');
      pill.addEventListener('click', () => {
        this.#switchTo(index);
        container.querySelectorAll('.sl-table__pill').forEach((p, i) => {
          p.classList.toggle('active', i === index);
          p.setAttribute('aria-pressed', i === index ? 'true' : 'false');
        });
      });
      container.append(pill);
    });
  }

  #buildCarousel() {
    const label = this.querySelector('[data-sl-label]');
    const prevBtn = this.querySelector('[data-sl-prev]');
    const nextBtn = this.querySelector('[data-sl-next]');
    const names = this.#optionNames();

    const update = () => {
      if (label) label.textContent = names[this.#currentIndex] || '';
    };
    update();

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        const next = this.#currentIndex > 0 ? this.#currentIndex - 1 : this.#images.length - 1;
        this.#switchTo(next, 'prev').then(update);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        const next = this.#currentIndex < this.#images.length - 1 ? this.#currentIndex + 1 : 0;
        this.#switchTo(next, 'next').then(update);
      });
    }
  }

  #buildDropdown() {
    const dropdown = this.querySelector('[data-sl-dropdown]');
    if (!dropdown) return;
    dropdown.replaceChildren();
    this.#optionNames().forEach((name, index) => {
      if (!name) return;
      const option = document.createElement('option');
      option.value = String(index);
      option.textContent = name;
      if (index === 0) option.selected = true;
      dropdown.append(option);
    });
    dropdown.addEventListener('change', () => {
      this.#switchTo(parseInt(dropdown.value, 10));
    });
  }

  /* ---------------- preload ---------------- */

  #preloadAll() {
    if (this.#preloaded) return;
    this.#images.forEach((img) => {
      if (img.src) new Image().src = img.src;
    });
    this.#preloaded = true;
  }

  #setupPreload() {
    if (!('IntersectionObserver' in window)) {
      this.#preloadAll();
      return;
    }
    this.#io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.#preloadAll();
            this.#io.disconnect();
          }
        });
      },
      { rootMargin: '200px' }
    );
    this.#io.observe(this);
  }

  /* ---------------- scroll-entrance reveal ---------------- */

  #setupReveal() {
    const style = this.dataset.reveal;
    if (!style || style === 'none') return;

    // Editor: always show immediately (Liquid also bakes .is-revealed in design
    // mode). Reduced-motion / no-IO: show instantly, no entrance.
    const designMode = window.Shopify && window.Shopify.designMode;
    if (designMode || this.#reducedMotion() || !('IntersectionObserver' in window)) {
      this.classList.add('is-revealed');
      return;
    }

    this.#revealIo = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.classList.add('is-revealed');
            obs.disconnect();
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
    );
    this.#revealIo.observe(this);
  }

  /* ---------------- image switching ---------------- */

  /**
   * @param {number} newIndex
   * @param {('next'|'prev')} [direction]
   * @param {boolean} [instant] — skip motion (Theme Editor jumps)
   * @returns {Promise<void>}
   */
  #switchTo(newIndex, direction, instant = false) {
    if (newIndex < 0 || newIndex >= this.#images.length) return Promise.resolve();
    if (this.#isAnimating || newIndex === this.#currentIndex) return Promise.resolve();

    const wrapper = this.querySelector('[data-sl-images]');
    const fromImg = this.#images[this.#currentIndex];
    const toImg = this.#images[newIndex];
    if (!wrapper || !fromImg || !toImg) return Promise.resolve();

    if (!direction) direction = newIndex > this.#currentIndex ? 'next' : 'prev';
    this.#currentIndex = newIndex;

    const finish = () => {
      fromImg.classList.remove('active');
      toImg.classList.add('active');
      this.#isAnimating = false;
    };

    const effect = this.dataset.transitionEffect;
    // Motion runs on GSAP when the theme already loads it; feature-detected
    // lazily so module-eval order vs a deferred GSAP bundle never matters. No
    // GSAP or reduced-motion → instant swap.
    if (instant || effect === 'none' || !window.gsap || this.#reducedMotion()) {
      finish();
      return Promise.resolve();
    }

    this.#isAnimating = true;
    return this.#animateSwap(wrapper, fromImg, toImg, {
      effect: effect,
      duration: parseInt(this.dataset.transitionDuration, 10) || 300,
      timing: this.dataset.transitionTiming,
      direction: direction,
    }).then(finish);
  }

  /**
   * GSAP image-swap choreography (self-contained; formerly SwitchMotion).
   * Locks the wrapper height, makes both images absolute (is-animating class),
   * animates out+in simultaneously, then releases. The caller swaps the .active
   * class after the returned promise resolves (same microtask → no flash).
   * @param {HTMLElement} wrapper — .sl-table__images
   * @param {HTMLElement} fromImg
   * @param {HTMLElement} toImg
   * @param {{effect: string, duration: number, timing: string, direction: string}} opts
   * @returns {Promise<void>}
   */
  #animateSwap(wrapper, fromImg, toImg, opts) {
    const gsap = window.gsap;
    if (!gsap) return Promise.resolve();

    const duration = (opts.duration || 300) / 1000;
    const ease = SL_TABLE_EASE_MAP[opts.timing] || 'power1.inOut';

    return new Promise((resolve) => {
      // Lock BOTH width and height before the images go position:absolute. Height
      // alone is not enough: <sl-table> is often a shrink-to-fit flex/grid item
      // (e.g. inside a hero __content-wrapper). Once every image leaves flow, that
      // ancestor collapses to its text/pill width, so the wrapper's width:100%
      // resolves smaller and the images visibly shrink mid-swap, then pop back to
      // full size when the swap ends. Pinning the wrapper's measured px width keeps
      // the whole in-flow chain (and the image size) stable through the animation.
      gsap.set(wrapper, { width: wrapper.offsetWidth, height: wrapper.offsetHeight });
      wrapper.classList.add('is-animating');
      fromImg.classList.add('animating-out');
      toImg.classList.add('animating-in');

      const tl = gsap.timeline({
        onComplete: () => {
          wrapper.classList.remove('is-animating');
          fromImg.classList.remove('animating-out');
          toImg.classList.remove('animating-in');
          gsap.set([fromImg, toImg], { clearProps: 'all' });
          gsap.set(wrapper, { clearProps: 'width,height' });
          resolve(); // caller swaps .active in the same microtask — no flash
        },
      });

      if (opts.effect === 'slide-left' || opts.effect === 'slide-right') {
        const enterFrom = opts.direction === 'next' ? 100 : -100;
        tl.fromTo(toImg, { xPercent: enterFrom }, { xPercent: 0, duration: duration, ease: ease }, 0)
          .to(fromImg, { xPercent: -enterFrom, duration: duration, ease: ease }, 0);
      } else if (opts.effect === 'fade') {
        tl.to(fromImg, { opacity: 0, duration: duration, ease: 'power1.out' }, 0)
          .fromTo(toImg, { opacity: 0 }, { opacity: 1, duration: duration * 0.7, ease: 'power1.in' }, duration * 0.3);
      } else if (opts.effect === 'scale') {
        tl.to(fromImg, { opacity: 0, scale: 0.9, duration: duration, ease: 'power1.out' }, 0)
          .fromTo(
            toImg,
            { opacity: 0, scale: 0.9 },
            { opacity: 1, scale: 1, duration: duration * 0.7, ease: 'power1.in' },
            duration * 0.3
          );
      } else {
        tl.set({}, {}, 0); // unknown effect → resolve via onComplete, instant
      }
    });
  }
}

if (!customElements.get('sl-table')) {
  customElements.define('sl-table', SLTable);
}
